/**
 * Icon-prefixed input for the auth hero pages (sign-in/register) —
 * deliberately its own small component rather than Carbon's `TextInput`.
 *
 * The auth pages are a scoped exception to the rest of the app's Carbon
 * conversion: a rounded, gradient, soft-shadow "front door" look the user
 * asked for by example, kept off every other (Carbon) screen on purpose —
 * see the top-of-file comment in `AuthLayout.tsx`. `TextInput` can't produce
 * this look without fighting its own flat-square styling, so this page's
 * fields are hand-built instead, styled by the `.auth-hero__field*` rules in
 * carbon.scss (searched for by that name, not by component).
 */
import { useId, useState, type ChangeEvent, type ReactNode } from 'react';
import { View, ViewOff, type CarbonIconType } from '@carbon/icons-react';

export interface AuthFieldProps {
  label: string;
  /** Icon rendered at the field's leading edge; omit for a field with nothing sensible to show (e.g. a bare name). */
  icon?: CarbonIconType;
  type?: 'text' | 'email' | 'tel' | 'password';
  name: string;
  autoComplete?: string;
  required?: boolean;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | undefined;
  /** An explicit id, for the rare case another element must reference this field — otherwise a stable generated one. */
  id?: string;
}

export function AuthField({
  label,
  icon: Icon,
  type = 'text',
  name,
  autoComplete,
  required = false,
  placeholder,
  value,
  onChange,
  error,
  id,
}: AuthFieldProps): React.JSX.Element {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const isPassword = type === 'password';
  const [revealed, setRevealed] = useState(false);

  function handleChange(event: ChangeEvent<HTMLInputElement>): void {
    onChange(event.target.value);
  }

  const trailing: ReactNode = isPassword ? (
    <button
      type="button"
      className="auth-hero__field-toggle"
      aria-label={revealed ? 'Hide password' : 'Show password'}
      onClick={() => setRevealed((current) => !current)}
    >
      {revealed ? <ViewOff size={18} /> : <View size={18} />}
    </button>
  ) : null;

  return (
    <div className="auth-hero__field">
      <label htmlFor={fieldId}>{label}</label>
      <div className={error !== undefined ? 'auth-hero__field-box auth-hero__field-box--invalid' : 'auth-hero__field-box'}>
        {Icon !== undefined && (
          <span className="auth-hero__field-icon" aria-hidden="true">
            <Icon size={18} />
          </span>
        )}
        <input
          id={fieldId}
          name={name}
          type={isPassword ? (revealed ? 'text' : 'password') : type}
          autoComplete={autoComplete}
          required={required}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          aria-invalid={error !== undefined}
          aria-describedby={error !== undefined ? `${fieldId}-error` : undefined}
        />
        {trailing}
      </div>
      {error !== undefined && (
        <p id={`${fieldId}-error`} className="auth-hero__field-error">
          {error}
        </p>
      )}
    </div>
  );
}

export default AuthField;
